import { Check, FileText } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoNote, Panel } from '@/components/common/Panel'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { usePositions } from '@/features/access/api'
import { usePersonelList } from '@/features/hr/personel/api'
import { useHrDepartments } from '@/features/hr/personel/api/referenceApi'
import { cn } from '@/lib/utils'
import { useDocumentTypes } from '../api'
import type { BelgeInfoValues } from '../schemas/belgeInfoSchema'


type Props = {
  info: BelgeInfoValues
  errors: Record<string, string>
  onChange: (patch: Partial<BelgeInfoValues>) => void
}

export function BelgeInfoStep({ info, errors, onChange }: Props) {
  const { t } = useTranslation()
  const types = useDocumentTypes()
  const departments = useHrDepartments().data ?? []
  const allPositions = usePositions()
  const people = usePersonelList()

  // Departman seçiliyse yalnız o departmanların pozisyonları listelenir; seçili kalanlar her durumda görünür.
  const positions = useMemo(
    () =>
      (allPositions.data ?? []).filter(
        (p) =>
          info.pozisyonlar.includes(p.id) ||
          (p.isActive && (!info.departmanIds.length || info.departmanIds.includes(p.departmentId))),
      ),
    [allPositions.data, info.departmanIds, info.pozisyonlar],
  )
  const reviewerOptions = useMemo(
    () => people.map((p) => ({ value: p.id, label: p.ad, description: [p.pozisyon, p.departman].filter(Boolean).join(' · '), keywords: [p.sicil] })),
    [people],
  )

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  const toggleDepartment = (id: string) => onChange({ departmanIds: toggle(info.departmanIds, id) })
  const togglePosition = (id: string) => onChange({ pozisyonlar: toggle(info.pozisyonlar, id) })

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <Panel icon={FileText} title={t('hr.belgeler.wizard.steps.info')} hint={t('hr.belgeler.wizard.hints.info')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ad">{t('hr.belgeler.form.name')}</Label>
            <Input id="ad" value={info.ad} onChange={(e) => onChange({ ad: e.target.value })} aria-invalid={!!errors.ad} />
            {errors.ad ? <p className="text-xs text-destructive">{errors.ad}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kat">{t('hr.belgeler.form.category')}</Label>
            <Select
              id="kat"
              value={info.kategori}
              onChange={(kategori) => onChange({ kategori })}
              loading={types.isLoading}
              options={(types.data ?? []).map((k) => ({ value: k.code, label: k.name }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="zor">{t('hr.belgeler.form.required')}</Label>
            <Select
              id="zor"
              value={info.zorunlu ? 'yes' : 'no'}
              onChange={(v) => onChange({ zorunlu: v === 'yes' })}
              options={[
                { value: 'yes', label: t('hr.belgeler.form.requiredYes') },
                { value: 'no', label: t('hr.belgeler.form.requiredNo') },
              ]}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="desc">{t('hr.belgeler.form.description')}</Label>
            <Textarea id="desc" rows={3} value={info.aciklama} onChange={(e) => onChange({ aciklama: e.target.value })} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>{t('hr.belgeler.form.departments')}</Label>
            <div className="flex flex-wrap gap-1.5">
              {(Array.isArray(departments) ? departments : []).map((d) => (
                <ToggleChip key={d.id} on={info.departmanIds.includes(d.id)} label={d.name} onClick={() => toggleDepartment(d.id)} />
              ))}
            </div>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>{t('hr.belgeler.form.positions')}</Label>
            {positions.length ? (
              <div className="flex flex-wrap gap-1.5">
                {positions.map((p) => (
                  <ToggleChip
                    key={p.id}
                    on={info.pozisyonlar.includes(p.id)}
                    label={info.departmanIds.length === 1 || !p.department ? p.title : `${p.title} · ${p.department.name}`}
                    onClick={() => togglePosition(p.id)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{allPositions.isLoading ? t('common.loading') : t('hr.belgeler.form.positionsEmpty')}</p>
            )}
            <p className="text-xs text-muted-foreground">{t('hr.belgeler.form.positionsHint')}</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ks">{t('hr.belgeler.form.owner')}</Label>
            <Select
              id="ks"
              value={info.kontrolSorumlusu}
              onChange={(kontrolSorumlusu) => onChange({ kontrolSorumlusu })}
              options={reviewerOptions}
              searchable
              placeholder={t('hr.belgeler.form.ownerPick')}
              invalid={!!errors.kontrolSorumlusu}
            />
            {errors.kontrolSorumlusu ? <p className="text-xs text-destructive">{errors.kontrolSorumlusu}</p> : null}
          </div>
        </div>
      </Panel>
      <InfoNote title={t('hr.belgeler.form.sideTitle')} className="h-fit">
        {t('hr.belgeler.form.sideBody')}
      </InfoNote>
    </div>
  )
}

function ToggleChip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        on ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/20' : 'bg-card text-muted-foreground hover:border-primary/25 hover:text-foreground',
      )}
    >
      <span className={cn('flex size-3.5 items-center justify-center rounded-[4px] border', on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40')}>
        {on ? <Check className="size-2.5" strokeWidth={3} /> : null}
      </span>
      {label}
    </button>
  )
}
